import apiClient from './api';
import { decodeJWT } from '../utils/jwt';

// Base notification interface matching backend CleanNotificationResponse
export interface ApiNotification {
  id: string;
  receiverId: string;
  receiverType: 'USER' | 'VENDOR' | 'ADMIN';
  status: 'UNREAD' | 'READ';
  timestamp: string;
  type: 'ORDER' | 'QUESTION' | 'REVIEW' | 'PAYMENT' | 'SYSTEM' | 'ALERT' | 'VENDOR_APPLICATION' | 'VENDOR_APPROVAL' | 'VENDOR_REJECTION';
  title: string;
  message: string;
  metadata?: Record<string, any>;
}

export interface NotificationsApiResponse {
  success: boolean;
  data: ApiNotification[];
  pagination: {
    page?: number;
    limit?: number;
    totalPages?: number;
    hasNext?: boolean;
    hasPrev?: boolean;
    lastEvaluatedKey?: string;
  };
}

// Legacy interface for backward compatibility (deprecated)
export interface AdminNotification {
  id: string;
  adminId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  status: 'UNREAD' | 'READ';
  createdAt: string;
  readAt?: string;
  actionUrl?: string;
  actionLabel?: string;
}

export interface NotificationsResponse {
  success: boolean;
  message: string;
  notifications: AdminNotification[];
  unreadCount: number;
}

export const notificationsService = {
  // Get admin notifications using the unified endpoint
  getAdminNotifications: async (status?: 'UNREAD' | 'READ'): Promise<NotificationsApiResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      
      const url = `/admins/notifications?${params.toString()}`;
      
      // Debug: Check adminId from localStorage and JWT token
      const adminIdFromStorage = localStorage.getItem('adminId');
      const token = localStorage.getItem('accessToken');
      let adminIdFromToken: string | null = null;
      
      if (token) {
        try {
          const decoded = decodeJWT(token);
          adminIdFromToken = decoded?.userId || decoded?.adminId || null;
          console.log('🔍 Decoded JWT token:', {
            userId: decoded?.userId,
            adminId: decoded?.adminId,
            email: decoded?.email,
            role: decoded?.role,
            fullDecoded: decoded
          });
        } catch (error) {
          console.error('❌ Failed to decode JWT token:', error);
        }
      }
      
      console.log('📤 Fetching notifications from unified endpoint:', { 
        status, 
        url,
        adminIdFromStorage,
        adminIdFromToken,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
        adminIdMismatch: adminIdFromStorage !== adminIdFromToken
      });
      
      const response = await apiClient.get(url);
      
      console.log('📢 Notifications API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        notificationCount: response.data?.data?.length || 0,
        pagination: response.data?.pagination,
        fullResponse: response.data
      });
      
      // Additional debugging for adminId mismatch
      if (response.data?.data?.length === 0 && adminIdFromToken) {
        console.warn('⚠️ No notifications found. Possible adminId mismatch:', {
          adminIdFromToken,
          adminIdFromStorage,
          suggestion: 'Check if notifications in database use a different adminId (e.g., "admin-001")',
          checkBackendLogs: 'Look for "Fetching notifications for receiver" log to see what adminId was used'
        });
      }
      
      // Return the full response structure matching vendor format
      return response.data as NotificationsApiResponse;
    } catch (error: any) {
      console.error('❌ Error fetching notifications:', {
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      
      // Return empty response structure on error
      return {
        success: false,
        data: [],
        pagination: {}
      };
    }
  },

  // Get all notifications (main method matching vendor interface)
  getNotifications: async (): Promise<NotificationsApiResponse> => {
    return await notificationsService.getAdminNotifications();
  },

  // Helper method to get just the notifications array (for backward compatibility)
  getNotificationsArray: async (status: 'UNREAD' | 'READ' | 'ALL' = 'UNREAD', page: number = 1, limit: number = 20, type?: string): Promise<ApiNotification[]> => {
    const response = await notificationsService.getAdminNotifications(status !== 'ALL' ? status : undefined);
    return response.data || [];
  },

  // Get both notifications and unread count in one call (backward compatibility)
  getNotificationsWithCount: async (status: 'UNREAD' | 'READ' | 'ALL' = 'UNREAD', page: number = 1, limit: number = 20, type?: string): Promise<{ notifications: ApiNotification[], unreadCount: number, totalCount: number }> => {
    const response = await notificationsService.getAdminNotifications(status !== 'ALL' ? status : undefined);
    const notifications = response.data || [];
    // Calculate unread count from notifications
    const unreadCount = notifications.filter(n => n.status === 'UNREAD').length;
    return {
      notifications,
      unreadCount,
      totalCount: notifications.length
    };
  },

  // Mark notification as read (no adminId needed - comes from authorizer)
  markAsRead: async (notificationId: string): Promise<boolean> => {
    try {
      const url = `/admins/notifications/${notificationId}/mark-read`;
      console.log('📤 Marking notification as read:', { notificationId, url });
      
      const response = await apiClient.put(url);
      
      console.log('✅ Mark as read API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        success: response.data?.success
      });
      
      return response.data?.success || true;
    } catch (error: any) {
      console.error('❌ Error marking notification as read:', {
        notificationId,
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      return false;
    }
  },

  // Mark all notifications as read using the unified endpoint
  markAllAsRead: async (type?: string): Promise<boolean> => {
    try {
      const url = `/admins/notifications/mark-all-read`;
      const requestBody = type ? { type } : {};
      
      console.log('📤 Marking all notifications as read:', { url, body: requestBody });
      
      const response = await apiClient.put(url, requestBody);
      
      console.log('✅ Mark all as read API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        success: response.data?.success
      });
      
      return response.data?.success || true;
    } catch (error: any) {
      console.error('❌ Error marking all notifications as read:', {
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      return false;
    }
  },

  // Delete notification
  deleteNotification: async (notificationId: string): Promise<boolean> => {
    try {
      const response = await apiClient.delete(`/notifications/${notificationId}`);
      return response.data?.success || true;
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
      return false;
    }
  },

  // Get unread count only (using the unified notifications endpoint)
  getUnreadCount: async (type?: string): Promise<number> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('status', 'UNREAD');
      if (type) params.append('type', type);
      
      const url = `/admins/notifications?${params.toString()}`;
      console.log('📤 Fetching unread notification count from unified endpoint:', { type, url });
      
      const response = await apiClient.get(url);
      
      console.log('📊 Unread count API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        notificationCount: response.data?.data?.length || 0
      });
      
      // Count unread notifications from response
      const notifications = response.data?.data || [];
      return notifications.filter((n: ApiNotification) => n.status === 'UNREAD').length;
    } catch (error: any) {
      console.error('❌ Error fetching unread notification count:', {
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      return 0;
    }
  },
};

export default notificationsService;